import { imageDimensionsFromData } from "image-dimensions";
//#region src/canonical-json.ts
async function digestCanonicalJson(value) {
	const digest = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(stringifyCanonicalJson(value)));
	return Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, "0")).join("");
}
function stringifyCanonicalJson(value) {
	const serialized = JSON.stringify(canonicalizeJsonValue(value));
	if (serialized === void 0) throw new TypeError("Value must be JSON-serializable.");
	return serialized;
}
function canonicalizeJsonValue(value) {
	if (value === null || typeof value === "boolean" || typeof value === "number" || typeof value === "string") return value;
	if (Array.isArray(value)) return value.map(canonicalizeJsonValue);
	if (value && typeof value === "object") return Object.fromEntries(Object.entries(value).filter(([, entry]) => entry !== void 0).sort(([leftKey], [rightKey]) => compareCanonicalJsonKeys(leftKey, rightKey)).map(([key, entry]) => [key, canonicalizeJsonValue(entry)]));
	throw new TypeError("Value must be JSON-serializable.");
}
function compareCanonicalJsonKeys(leftKey, rightKey) {
	if (leftKey < rightKey) return -1;
	if (leftKey > rightKey) return 1;
	return 0;
}
//#endregion
//#region src/image-dimensions.ts
const IMAGE_METADATA_MAX_BYTES = 128 * 1024;
const IMAGE_METADATA_MAX_READS = 16;
const MAX_DIMENSION = 32768;
const MAX_PIXELS = 128 * 1024 * 1024;
const decoder = new TextDecoder("ascii");
var UnsupportedImageMetadata = class extends Error {};
/**
* Header-only display geometry. Never decodes, resizes, or buffers the original.
* Blob.slice and private R2 ranges share this bounded reader. Unsupported or
* ambiguous orientation has no geometry rather than an incorrect aspect ratio.
*/
async function readDisplayImageDimensions(source) {
	if (!Number.isSafeInteger(source.size) || source.size < 10) return;
	let bytesRead = 0;
	let reads = 0;
	let prefix = /* @__PURE__ */ new Uint8Array(0);
	const read = async (offset, length) => {
		if (!(Number.isSafeInteger(offset) && Number.isSafeInteger(length)) || offset < 0 || length < 1 || offset + length > source.size) throw new UnsupportedImageMetadata();
		if (offset + length <= prefix.length) return prefix.subarray(offset, offset + length);
		bytesRead += length;
		reads += 1;
		if (bytesRead > 131072 || reads > 16) throw new UnsupportedImageMetadata();
		const result = await source.read(offset, length);
		if (result.length !== length) throw new UnsupportedImageMetadata();
		return result;
	};
	try {
		prefix = (await read(0, Math.min(source.size, 64 * 1024))).slice();
		const raw = imageDimensionsFromData(prefix);
		if (!(raw && Number.isInteger(raw.width) && Number.isInteger(raw.height)) || raw.width < 1 || raw.height < 1 || raw.width > MAX_DIMENSION || raw.height > MAX_DIMENSION || raw.width * raw.height > MAX_PIXELS) return;
		let orientation;
		switch (raw.type) {
			case "gif":
				orientation = 1;
				break;
			case "jpeg":
				orientation = await readJpegOrientation(read);
				break;
			case "png":
				orientation = await readChunkOrientation(read, source.size, false);
				break;
			case "webp":
				orientation = decoder.decode(prefix.subarray(12, 16)) === "VP8X" && ((prefix[20] ?? 0) & 8) !== 0 ? await readChunkOrientation(read, source.size, true) : 1;
				break;
			default: return;
		}
		return orientation >= 5 ? {
			width: raw.height,
			height: raw.width
		} : {
			width: raw.width,
			height: raw.height
		};
	} catch (error) {
		if (error instanceof UnsupportedImageMetadata || error instanceof RangeError) return;
		throw error;
	}
}
function view(bytes) {
	return new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
}
async function readJpegOrientation(read) {
	let offset = 2;
	let orientation = 1;
	let seenExif = false;
	for (let count = 0; count < 256; count += 1) {
		const header = await read(offset, 4);
		if (header[0] !== 255) throw new UnsupportedImageMetadata();
		if (header[1] === 255) {
			offset += 1;
			continue;
		}
		if (header[1] === 218 || header[1] === 217) return orientation;
		const size = view(header).getUint16(2);
		if (size < 2) throw new UnsupportedImageMetadata();
		if (header[1] === 225) {
			const bytes = await read(offset + 4, size - 2);
			if (decoder.decode(bytes.subarray(0, 6)) === "Exif\0\0") {
				if (seenExif) throw new UnsupportedImageMetadata();
				seenExif = true;
				orientation = readTiffOrientation(bytes.subarray(6));
			}
		}
		offset += size + 2;
	}
	throw new UnsupportedImageMetadata();
}
async function readChunkOrientation(read, size, webp) {
	const typeOffset = webp ? 0 : 4;
	const lengthOffset = webp ? 4 : 0;
	let offset = webp ? 12 : 8;
	let orientation = 1;
	let seenExif = false;
	for (let count = 0; count < 256 && offset + 8 <= size; count += 1) {
		const header = await read(offset, 8);
		const length = view(header).getUint32(lengthOffset, webp);
		const kind = decoder.decode(header.subarray(typeOffset, typeOffset + 4));
		const next = offset + 8 + length + (webp ? length % 2 : 4);
		if (next > size) throw new UnsupportedImageMetadata();
		if (kind === "eXIf" || kind === "EXIF") {
			if (seenExif) throw new UnsupportedImageMetadata();
			seenExif = true;
			orientation = readExifOrientation(await read(offset + 8, length));
		}
		if (!webp && kind === "IEND") return orientation;
		offset = next;
	}
	if (webp && seenExif && offset === size) return orientation;
	throw new UnsupportedImageMetadata();
}
function readExifOrientation(bytes) {
	return readTiffOrientation(decoder.decode(bytes.subarray(0, 6)) === "Exif\0\0" ? bytes.subarray(6) : bytes);
}
/** TIFF IFD0 only. Never follows embedded thumbnails, GPS, or other IFD links. */
function readTiffOrientation(bytes) {
	const header = decoder.decode(bytes.subarray(0, 2));
	if (header !== "II" && header !== "MM") throw new UnsupportedImageMetadata();
	const littleEndian = header === "II";
	const data = view(bytes);
	if (data.getUint16(2, littleEndian) !== 42) throw new UnsupportedImageMetadata();
	const offset = data.getUint32(4, littleEndian);
	const count = data.getUint16(offset, littleEndian);
	if (count > 512 || offset + 2 + count * 12 > bytes.length) throw new UnsupportedImageMetadata();
	for (let index = 0; index < count; index += 1) {
		const entry = offset + 2 + index * 12;
		if (data.getUint16(entry, littleEndian) !== 274) continue;
		if (data.getUint16(entry + 2, littleEndian) !== 3 || data.getUint32(entry + 4, littleEndian) !== 1) throw new UnsupportedImageMetadata();
		const orientation = data.getUint16(entry + 8, littleEndian);
		if (orientation < 1 || orientation > 8) throw new UnsupportedImageMetadata();
		return orientation;
	}
	return 1;
}
//#endregion
//#region src/index.ts
function sleep(ms) {
	return new Promise((resolve) => {
		setTimeout(resolve, ms);
	});
}
async function retryWithBackoff(operation, options = {}) {
	const maxAttempts = normalizePositiveInteger(options.maxAttempts, 3);
	const initialDelayMs = normalizeNonNegativeInteger(options.initialDelayMs, 100);
	const backoffFactor = normalizePositiveNumber(options.backoffFactor, 2);
	let delayMs = initialDelayMs;
	for (let attempt = 1; attempt <= maxAttempts; attempt += 1) try {
		return await operation();
	} catch (error) {
		if (attempt >= maxAttempts || options.shouldRetry?.(error, attempt) === false) throw error;
		await sleep(delayMs);
		delayMs = Math.trunc(delayMs * backoffFactor);
	}
	throw new Error("retryWithBackoff exhausted without returning or throwing");
}
function toErrorMessage(error) {
	if (error instanceof Error) return error.message;
	return String(error);
}
function normalizePositiveInteger(value, fallback) {
	if (typeof value !== "number" || !Number.isFinite(value)) return fallback;
	return Math.max(1, Math.trunc(value));
}
function normalizeNonNegativeInteger(value, fallback) {
	if (typeof value !== "number" || !Number.isFinite(value)) return fallback;
	return Math.max(0, Math.trunc(value));
}
function normalizePositiveNumber(value, fallback) {
	if (typeof value !== "number" || !Number.isFinite(value) || value <= 0) return fallback;
	return value;
}
//#endregion
export { IMAGE_METADATA_MAX_BYTES, IMAGE_METADATA_MAX_READS, digestCanonicalJson, readDisplayImageDimensions, retryWithBackoff, sleep, stringifyCanonicalJson, toErrorMessage };

//# sourceMappingURL=index.js.map